import React from 'react';
import { captureException } from '../lib/posthog';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Erreur capturée:', error, errorInfo);

    captureException(error, { componentStack: errorInfo.componentStack ?? undefined });

    // Chunk introuvable après un nouveau déploiement → recharger une seule fois pour récupérer les nouveaux assets
    const isChunkError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Importing a module script failed') ||
      error?.name === 'ChunkLoadError';

    if (isChunkError) {
      const reloadKey = '__chunk_reload_attempted__';
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1');
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-8">
          <div className="text-4xl">⚠️</div>
          <h2 className="text-xl font-semibold text-content-primary">
            Une erreur inattendue est survenue
          </h2>
          <p className="text-content-secondary text-center max-w-md">
            La page a rencontré un problème. Vos données ont été sauvegardées automatiquement.
          </p>
          <div className="flex gap-3 mt-2">
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
            >
              Réessayer
            </button>
            <button
              onClick={() => window.location.href = '/journal'}
              className="px-4 py-2 bg-white/[0.06] text-content-primary rounded-lg hover:bg-white/[0.1] transition-colors"
            >
              Retour au journal
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
